import type {
  PillsToolbarItem,
  SearchToolbarItem,
  SelectToolbarItem,
} from '../FilterToolbar'
import { useCallback, useMemo } from 'react'

/**
 * One filter of a list screen. Declaring it here is what makes the default,
 * the URL round-trip, the API param, the reset and the toolbar item agree with
 * each other instead of being restated per screen.
 */
export type FilterDef<V> = {
  /**
   * Value with no query param present, and what `reset` restores. Not
   * necessarily "empty" — Matching defaults to active mandates, Projects to
   * the statuses staff are actively working on. Compared by identity, so keep
   * it a primitive.
   */
  defaultValue: V
  /**
   * Widest value, the one that narrows nothing. Only needed where the default
   * is itself a filter — a screen opening on active records clears to "all",
   * so `isCleared` and `clearAll` are not the same as `reset`.
   */
  clearedValue?: V
  /** Raw URL value to filter value. Must tolerate anything a bookmark holds. */
  parse: (raw: unknown) => V
  /**
   * Filter value to API query param, for filters whose wire form differs from
   * their URL form — a sentinel expanding into the concrete filter the API
   * expects. Not called for the value that narrows nothing, which is never
   * sent.
   */
  toQuery?: (value: V) => string | undefined
  /**
   * Whether a non-default value counts as "the list is filtered". Sort order
   * and the open row live in the URL alongside the filters but do not narrow
   * the list, so they set this to `false`.
   */
  countsAsFilter?: boolean
  /** Whether `reset` restores the default. The open row survives a reset. */
  resettable?: boolean
}

// `any` on purpose: a schema mixes filters of different value types, and
// `unknown` makes `toQuery`'s parameter contravariant so `FilterDef<string>`
// stops being assignable to the constraint.

export type FilterSchema = Record<string, FilterDef<any>>

export type FilterValues<S extends FilterSchema> = {
  [K in keyof S]: S[K] extends FilterDef<infer V> ? V : never
}

/**
 * The open tab of a detail pane, which every list route carries.
 *
 * Reserved here rather than declared per screen: `listSearchValidator` builds
 * the search from the keys it knows, so a screen that forgot to name it would
 * have its pane's writes dropped on the next navigation with nothing to say
 * so. `PaneTabs` reads and writes it; no filter schema mentions it.
 *
 * Not `tab`: Planning carries its page's own tab under that name.
 */
export const PANE_TAB_PARAM = 'pane'

type PaneTabSearch = { [K in typeof PANE_TAB_PARAM]?: string }

/** What the URL carries: every filter, absent wherever it is at its default. */
export type FilterSearch<S extends FilterSchema> = Partial<FilterValues<S>>

export type ListFilters<S extends FilterSchema> = {
  values: FilterValues<S>
  set: <K extends keyof S>(key: K, value: FilterValues<S>[K]) => void
  /** Several filters in one go, for a control that moves more than one.
   *  `push` leaves a history entry, for a change that is a navigation. */
  setMany: (
    values: Partial<FilterValues<S>>,
    options?: { push?: boolean },
  ) => void
  reset: () => void
  hasActiveFilters: boolean
  /** Widens every filter that narrows the list, past the default view. */
  clearAll: () => void
  /** Whether nothing narrows the list — what the leading "Alle" pill shows. */
  isCleared: boolean
  /** Every filter in its API form, ready to hand to a list query. */
  queryParams: { [K in keyof S]: string | undefined }
  toolbarItem: ToolbarItemFactories<S>
}

type ToolbarItemFactories<S extends FilterSchema> = {
  pills: <K extends keyof S>(
    key: K,
    options: { value: FilterValues<S>[K]; label: string }[],
  ) => PillsToolbarItem<FilterValues<S>[K]>
  select: <K extends keyof S>(
    key: K,
    options: { value: string; label: string }[],
    opts?: { placeholder?: string },
  ) => SelectToolbarItem
  search: <K extends keyof S>(
    key: K,
    opts?: { placeholder?: string; width?: string },
  ) => SearchToolbarItem
}

const clearedValueOf = <V>(def: FilterDef<V>): V =>
  def.clearedValue !== undefined ? def.clearedValue : def.defaultValue

/**
 * `validateSearch` for a list route.
 *
 * A value equal to its default is dropped, so the address of an unfiltered
 * list is the bare path — and two ways of spelling the same view cannot exist.
 */
export function listSearchValidator<S extends FilterSchema>(schema: S) {
  return (raw: Record<string, unknown>): FilterSearch<S> & PaneTabSearch => {
    const search: Record<string, unknown> = {}
    const pane = raw[PANE_TAB_PARAM]
    if (typeof pane === 'string' && pane.length > 0)
      search[PANE_TAB_PARAM] = pane
    for (const key in schema) {
      const def = schema[key]
      if (!def) continue
      const value = def.parse(raw[key])
      if (value !== def.defaultValue) search[key] = value
    }
    return search as FilterSearch<S> & PaneTabSearch
  }
}

type NavigateFn<S extends FilterSchema> = (opts: {
  search: (prev: FilterSearch<S>) => FilterSearch<S>
  replace?: boolean
}) => void

/**
 * Filter state for an Internal list screen, held in the page URL.
 *
 * The URL is the state: `search` comes from the route's validated params and
 * every change navigates. That is what makes a filtered list linkable, gives
 * it a working back button, and keeps two controls reading the same filter
 * from drifting apart.
 */
/** The two hooks every route reads its filters through. */
type FilterRoute<S extends FilterSchema> = {
  useSearch: () => FilterSearch<S>
  useNavigate: () => NavigateFn<S>
}

/**
 * The screen's filters, read from the URL and written back to it.
 *
 * Takes the route object rather than its two hooks: every caller passed
 * `Route.useSearch()` and `Route.useNavigate()`, in that order, and the pair
 * is what the route already is.
 */
export function useListFilters<S extends FilterSchema>(
  schema: S,
  route: FilterRoute<S>,
): ListFilters<S> {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const values = useMemo(() => {
    const resolved = {} as FilterValues<S>
    for (const key in schema) {
      const def = schema[key]
      if (!def) continue
      const fromUrl = search[key]
      resolved[key] = (
        fromUrl === undefined ? def.defaultValue : fromUrl
      ) as FilterValues<S>[typeof key]
    }
    return resolved
  }, [schema, search])

  // Replaces by default: typing in the search box or flipping a pill must not
  // stack a history entry per keystroke. `push` is for the other kind of
  // change — one that takes the reader somewhere else, which the back button
  // has to be able to undo.
  const setMany = useCallback(
    (partial: Partial<FilterValues<S>>, options?: { push?: boolean }) => {
      navigate({
        search: prev => {
          const next = { ...prev }
          for (const key in partial) {
            const def = schema[key]
            const value = partial[key]
            if (!def) continue
            // Through `parse`, so a fresh empty list is the shared empty
            // default and stays out of the address.
            const parsed = def.parse(value)
            if (parsed === def.defaultValue) delete next[key]
            else next[key] = parsed
          }
          return next
        },
        replace: !options?.push,
      })
    },
    [navigate, schema],
  )

  const set = useCallback(
    <K extends keyof S>(key: K, value: FilterValues<S>[K]) => {
      setMany({ [key]: value } as Partial<FilterValues<S>>)
    },
    [setMany],
  )

  const reset = useCallback(() => {
    const next = {} as Partial<FilterValues<S>>
    for (const key in schema) {
      const def = schema[key]
      if (!def || def.resettable === false) continue
      next[key] = def.defaultValue as FilterValues<S>[typeof key]
    }
    setMany(next)
  }, [schema, setMany])

  const clearAll = useCallback(() => {
    const next = {} as Partial<FilterValues<S>>
    for (const key in schema) {
      const def = schema[key]
      if (!def || def.countsAsFilter === false) continue
      next[key] = clearedValueOf(def) as FilterValues<S>[typeof key]
    }
    setMany(next)
  }, [schema, setMany])

  const hasActiveFilters = useMemo(
    () =>
      Object.keys(schema).some(key => {
        const def = schema[key]
        return (
          def?.countsAsFilter !== false && values[key] !== def?.defaultValue
        )
      }),
    [schema, values],
  )

  const isCleared = useMemo(
    () =>
      Object.keys(schema).every(key => {
        const def = schema[key]
        return (
          !def ||
          def.countsAsFilter === false ||
          values[key] === clearedValueOf(def)
        )
      }),
    [schema, values],
  )

  const queryParams = useMemo(() => {
    const params = {} as { [K in keyof S]: string | undefined }
    for (const key in schema) {
      const def = schema[key]
      if (!def) continue
      const value = values[key]
      // An omitted parameter is how the wire says "all of them", so the value
      // that narrows nothing is never sent. Everything else goes, through
      // `toQuery` where the filter has one.
      params[key] =
        value === clearedValueOf(def)
          ? undefined
          : def.toQuery
            ? def.toQuery(value)
            : String(value)
    }
    return params
  }, [schema, values])

  const toolbarItem = useMemo<ToolbarItemFactories<S>>(
    () => ({
      pills: (key, options) => ({
        type: 'pills',
        options,
        value: values[key],
        onChange: value => set(key, value),
      }),
      select: (key, options, opts) => ({
        type: 'select',
        options,
        value: values[key] as string,
        onChange: value => set(key, value as FilterValues<S>[typeof key]),
        ...opts,
      }),
      search: (key, opts) => ({
        type: 'search',
        value: values[key] as string,
        onChange: value => set(key, value as FilterValues<S>[typeof key]),
        ...opts,
      }),
    }),
    [values, set],
  )

  return useMemo(
    () => ({
      values,
      set,
      setMany,
      reset,
      hasActiveFilters,
      clearAll,
      isCleared,
      queryParams,
      toolbarItem,
    }),
    [
      values,
      set,
      setMany,
      reset,
      hasActiveFilters,
      clearAll,
      isCleared,
      queryParams,
      toolbarItem,
    ],
  )
}

// ─── Filter kinds ───────────────────────────────────────────────────────────

/**
 * What a filter carries while it narrows nothing.
 *
 * One string wherever a list spells its widest value, so a hand-written
 * address reads the same whichever filter it names. Paired with `ALL_OPTION`,
 * which is this value under the word a select shows for it. A filter whose
 * widest value is simply an absent parameter uses `undefined` instead.
 */
export const FILTER_ALL = 'all'

/** A filter's own values, plus the sentinel that stands for all of them. */
export type AllOr<V extends string> = V | typeof FILTER_ALL

/** The plain string filter every search box and "all"-sentinel select needs. */
export function stringFilter<V extends string = string>(
  defaultValue: V = '' as V,
): FilterDef<V> {
  return {
    defaultValue,
    parse: raw => (typeof raw === 'string' ? (raw as V) : defaultValue),
  }
}

/**
 * A filter over a fixed set of values — an enum object or a literal list.
 * Anything else in the URL falls back to the default, so a stale bookmark
 * lands on a valid view instead of an empty one.
 */
export function oneOf<V>(
  allowed: readonly string[] | Record<string, string>,
  defaultValue: V,
): FilterDef<V> {
  const values = Array.isArray(allowed) ? allowed : Object.values(allowed)

  return {
    defaultValue,
    parse: raw =>
      typeof raw === 'string' && values.includes(raw)
        ? (raw as V)
        : defaultValue,
  }
}

/**
 * A switch. TanStack's search serialisation carries the boolean itself, so a
 * bookmark holding the string `"true"` — hand-edited, or from an older link —
 * still reads as on.
 */
export function boolFilter(defaultValue = false): FilterDef<boolean> {
  return {
    defaultValue,
    parse: raw =>
      typeof raw === 'boolean' ? raw : raw === 'true' ? true : defaultValue,
  }
}

/**
 * The row whose detail pane is open.
 *
 * It rides in the query string so the link stays reloadable, but it narrows
 * nothing and survives a filter reset.
 */
export function selectedRowFilter(): FilterDef<number | null> {
  return {
    defaultValue: null,
    parse: raw => {
      const value = Number(raw)
      return Number.isInteger(value) && value > 0 ? value : null
    },
    countsAsFilter: false,
    resettable: false,
  }
}

/**
 * The open row of a list whose keys are not all record ids — a tree that mixes
 * an entity's rows with fixed entries of its own, where `house` and `12` are
 * both addresses of something the pane can show.
 */
export function selectedKeyFilter(): FilterDef<string | null> {
  return {
    defaultValue: null,
    parse: raw => (typeof raw === 'string' && raw.length > 0 ? raw : null),
    countsAsFilter: false,
    resettable: false,
  }
}

/** One shared empty list, so "nothing" compares equal to the default and
 *  stays out of the address. */
const NONE: readonly never[] = []

/**
 * A single-value filter lifted over a list: the rows of a list where more
 * than one may be open at once — lines that fold open in place and stay
 * open while another is opened. Each entry is parsed as the one filter
 * parses it, and what that filter rejects is dropped.
 */
export function manyOf<V>(one: FilterDef<V | null>): FilterDef<readonly V[]> {
  return {
    defaultValue: NONE,
    parse: raw => {
      const values = (Array.isArray(raw) ? raw : [raw])
        .map(entry => one.parse(entry))
        .filter((value): value is V => value !== null)
      return values.length > 0 ? values : NONE
    },
    countsAsFilter: one.countsAsFilter,
    resettable: one.resettable,
  }
}
